import {
  CheckIcon,
  CopyIcon,
  InfoIcon,
  Loader2Icon,
  XIcon,
} from 'lucide-react';
import { useEffect, useState, type JSX } from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Button } from '@/components/ui/button.js';
import { DialogClose } from '@/components/ui/dialog.js';
import { copyToClipboard } from '@/lib/clipboard.js';

import type { ExternalTenantConsent } from '../../services/integration-api.js';

/** Whose directory the new connection lives in. */
type TenantChoice = 'corporate' | 'customer';

export interface SharePointTenantChoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Whether this deployment holds the multi-tenant application a customer's directory needs. It comes
   * from the presence read (`external_tenant_lane_available`), so the option turns on by itself the
   * moment the application is configured — no new build.
   */
  externalTenantLaneAvailable: boolean;
  /**
   * The customer directories whose administrators have already approved us for this workspace. Empty
   * means the approval has not come back, which is what makes this screen offer the link instead of a
   * sign-in.
   */
  approvedTenants: ExternalTenantConsent[];
  /** Disables the actions while the OAuth popup is in flight. */
  busy?: boolean;
  /**
   * Called when the person dismisses the dialog — a click outside or Escape — while it is busy. Given,
   * the dismiss cancels that work and closes the dialog; absent, a busy dialog refuses to close.
   */
  onDismissWhileBusy?: () => void;
  /** Run the corporate connect. */
  onConfirmCorporate: () => void | Promise<void>;
  /** Sign in to the named customer directory. */
  onConfirmExternal: (tenantId: string) => void | Promise<void>;
  /** Ask the platform for the approval link, or null when the request failed. */
  onRequestApprovalLink: () => Promise<string | null>;
  /** Re-read the workspace's approvals, for when the administrator has just said yes. */
  onCheckForApproval: () => Promise<void>;
}

/**
 * Which SharePoint a shared workspace is about to connect: `Internal` — ours — or `External`, a
 * customer's own directory.
 *
 * The choice is not there because the platform could not work the tenant out for itself — it is there
 * because the two go through different steps. Internal connects by signing in; External needs the
 * customer's own administrator to approve access for Customertimes first. Without being asked, someone
 * would take the sign-in path and hit "ask your administrator" with nothing to do about it.
 *
 * So External is a two-visit flow, and the screen says which visit this is. Before any approval it
 * hands over the link and nothing else: the member sends it to the administrator themselves, because
 * we have no channel into another organization. Once an approval is on record the same option becomes
 * an ordinary sign-in, with the account the customer issued.
 *
 * Where the deployment holds no multi-tenant application the option is shown switched off rather than
 * hidden, so the team can see the mechanism exists — the reason it is off is a fact about our rollout
 * and not something the person reading this dialog can act on.
 *
 * A personal workspace never sees this dialog: a personal connection is always in our own tenant.
 */
export function SharePointTenantChoiceDialog({
  open,
  onOpenChange,
  externalTenantLaneAvailable,
  approvedTenants,
  busy = false,
  onDismissWhileBusy,
  onConfirmCorporate,
  onConfirmExternal,
  onRequestApprovalLink,
  onCheckForApproval,
}: SharePointTenantChoiceDialogProps): JSX.Element {
  const [choice, setChoice] = useState<TenantChoice>('corporate');
  const [approvalLink, setApprovalLink] = useState<string | null>(null);
  const [requestingLink, setRequestingLink] = useState(false);
  const [checkingApproval, setCheckingApproval] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);

  /**
   * Reset on OPEN only. `approvedTenants` must stay out of this: an approval
   * arriving while the dialog is open would otherwise throw the person back to
   * Internal and discard both the link they were about to send and a deliberate
   * choice of a second directory — the moment the screen exists to handle.
   */
  useEffect(() => {
    if (!open) return;
    setChoice('corporate');
    setApprovalLink(null);
    setSelectedTenant(null);
  }, [open]);

  const hasApproval = approvedTenants.length > 0;
  const customerSelectable = externalTenantLaneAvailable;
  /**
   * The directory Continue is for: what the person picked, or the first approval
   * while they have picked nothing. Deriving it rather than seeding state is what
   * lets a newly arrived approval become selectable without resetting anything.
   */
  const targetTenant =
    approvedTenants.find((consent) => consent.tenantId === selectedTenant)
      ?.tenantId ?? approvedTenants[0]?.tenantId;

  const requestLink = async (): Promise<void> => {
    setRequestingLink(true);
    try {
      setApprovalLink(await onRequestApprovalLink());
    } finally {
      setRequestingLink(false);
    }
  };

  /**
   * Re-reads the approvals. The presence query has a stale window and no polling,
   * so without this the screen's promise — come back here and it will say so —
   * would depend on the person happening to reopen the page late enough.
   */
  const checkForApproval = async (): Promise<void> => {
    setCheckingApproval(true);
    try {
      await onCheckForApproval();
    } finally {
      setCheckingApproval(false);
    }
  };

  /**
   * Continue does whatever the chosen lane's next step is. On the customer lane before any approval
   * there is no next step here — the administrator's is next — so it stays unavailable rather than
   * firing a request nowhere.
   */
  const confirmDisabled =
    busy || (choice === 'customer' && (!customerSelectable || !hasApproval));

  const confirm = (): void => {
    if (choice === 'corporate') {
      void onConfirmCorporate();
      return;
    }
    if (targetTenant) void onConfirmExternal(targetTenant);
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={(next) => {
        if (busy && !next) {
          if (!onDismissWhileBusy) return;
          onDismissWhileBusy();
        }
        onOpenChange(next);
      }}
      title="Which SharePoint are you connecting?"
      description="The two go through different steps, so pick one before signing in."
      titleHidden
      showCloseButton={false}
      className="flex w-full max-w-[var(--dialog-w-sm)] flex-col gap-0 overflow-hidden rounded-xl border-border/50 p-0 shadow-xl"
    >
      <div className="px-6 pt-6 pb-2">
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-lg font-semibold tracking-tight text-foreground">
            Which SharePoint are you connecting?
          </h3>
          <DialogClose asChild>
            <button
              type="button"
              aria-label="Close"
              disabled={busy}
              className="-mt-1 -mr-1 flex size-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-foreground/10 hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
            >
              <XIcon className="size-4" />
            </button>
          </DialogClose>
        </div>

        <fieldset className="mt-4 flex flex-col gap-4 border-0 p-0">
          <legend className="text-compact leading-relaxed text-muted-foreground">
            The two go through different steps, so pick one before signing in.
          </legend>

          <label
            htmlFor="sharepoint-tenant-corporate"
            className="flex cursor-pointer items-start gap-2"
          >
            <input
              id="sharepoint-tenant-corporate"
              type="radio"
              name="sharepoint-tenant"
              value="corporate"
              checked={choice === 'corporate'}
              onChange={() => setChoice('corporate')}
              disabled={busy}
              className="mt-0.5 size-4 shrink-0 accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-medium text-foreground">
                Internal
              </span>
              <span className="text-caption leading-relaxed text-muted-foreground">
                You sign in with your Customertimes account and the workspace is
                connected right away.
              </span>
            </span>
          </label>

          <label
            htmlFor="sharepoint-tenant-customer"
            className={
              customerSelectable
                ? 'flex cursor-pointer items-start gap-2'
                : 'flex items-start gap-2 opacity-60'
            }
          >
            <input
              id="sharepoint-tenant-customer"
              type="radio"
              name="sharepoint-tenant"
              value="customer"
              checked={choice === 'customer'}
              onChange={() => setChoice('customer')}
              disabled={busy || !customerSelectable}
              aria-describedby="sharepoint-tenant-customer-note"
              className="mt-0.5 size-4 shrink-0 accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-foreground/40"
            />
            <span className="flex flex-col gap-1">
              <span className="text-sm font-medium text-foreground">
                External
              </span>
              <span
                id="sharepoint-tenant-customer-note"
                className="text-caption leading-relaxed text-muted-foreground"
              >
                {!customerSelectable
                  ? 'Coming soon.'
                  : hasApproval
                    ? 'Approved by their administrator. Sign in with the account the customer issued you.'
                    : 'Their administrator has to approve access for Customertimes first. Send them the link below.'}
              </span>
            </span>
          </label>
        </fieldset>

        {choice === 'customer' && customerSelectable && !hasApproval && (
          <div className="mt-4 flex flex-col gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
            <p className="text-caption leading-relaxed text-foreground">
              Send this link to the customer&apos;s Microsoft 365 administrator,
              and ask them for an account in their SharePoint — signing in needs
              one. Once they approve the link, come back here and connect: this
              screen will say so.
            </p>
            {approvalLink ? (
              <>
                <div className="flex items-center gap-2">
                  <code className="min-w-0 flex-1 truncate rounded bg-background px-2 py-1 text-caption text-muted-foreground">
                    {approvalLink}
                  </code>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      void copyToClipboard(approvalLink, 'Approval link copied')
                    }
                  >
                    <CopyIcon className="size-3.5" aria-hidden="true" />
                    Copy
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="self-start"
                  onClick={() => void checkForApproval()}
                  disabled={checkingApproval || busy}
                >
                  {checkingApproval && (
                    <Loader2Icon
                      className="size-3.5 animate-spin"
                      aria-hidden="true"
                    />
                  )}
                  Check for approval
                </Button>
              </>
            ) : (
              <Button
                variant="outline"
                size="sm"
                className="self-start"
                onClick={() => void requestLink()}
                disabled={requestingLink || busy}
              >
                {requestingLink && (
                  <Loader2Icon
                    className="size-3.5 animate-spin"
                    aria-hidden="true"
                  />
                )}
                Get approval link
              </Button>
            )}
          </div>
        )}

        {choice === 'customer' && customerSelectable && hasApproval && (
          <div className="mt-4 flex flex-col gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
            <p className="flex items-start gap-2 text-caption leading-relaxed text-foreground">
              <CheckIcon
                className="mt-0.5 size-4 shrink-0 text-muted-foreground"
                aria-hidden="true"
              />
              <span>
                Access is approved. Continue signs you in to the customer&apos;s
                SharePoint.
              </span>
            </p>
            {approvedTenants.length > 1 && (
              <label
                htmlFor="sharepoint-approved-tenant"
                className="flex flex-col gap-1 text-caption text-muted-foreground"
              >
                Which customer directory
                <select
                  id="sharepoint-approved-tenant"
                  value={targetTenant ?? ''}
                  onChange={(event) => setSelectedTenant(event.target.value)}
                  disabled={busy}
                  className="rounded border border-border bg-background px-2 py-1 text-caption text-foreground"
                >
                  {approvedTenants.map((consent) => (
                    <option key={consent.tenantId} value={consent.tenantId}>
                      {consent.tenantId}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        )}

        {choice === 'customer' && !customerSelectable && (
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2.5">
            <InfoIcon
              className="mt-0.5 size-4 shrink-0 text-muted-foreground"
              aria-hidden="true"
            />
            <p className="text-caption leading-relaxed text-foreground">
              Nothing to do here yet.
            </p>
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 px-6 pb-5 pt-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onOpenChange(false)}
          disabled={busy}
        >
          Cancel
        </Button>
        <Button
          variant="default"
          size="sm"
          onClick={confirm}
          disabled={confirmDisabled}
        >
          {busy && (
            <Loader2Icon className="size-3.5 animate-spin" aria-hidden="true" />
          )}
          Continue
        </Button>
      </div>
    </AppDialog>
  );
}
